package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.ProfileItem
import co.uk.sacursoftware.ssinventory.data.model.SaveProfileRequest
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.AppSession
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProfileScreen(
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    val initialItems = AppSession.userProfile ?: emptyList()
    var fieldIndexToRemove by remember { mutableStateOf<Int?>(null) }
    val profilePairs = remember {
        mutableStateListOf<Pair<String, String>>().apply {
            if (initialItems.isNotEmpty()) {
                addAll(initialItems.map { it.type to (it.data ?: "") })
            } else {
                add("shopName" to "")
                add("email" to "")
                add("secondaryEmail" to "")
                add("address" to "")
                add("phone" to "")
            }
        }
    }

    var isLoading by remember { mutableStateOf(false) }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Profile & Settings") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text(
                    "Profile Settings (Key-Value Pairs)",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.secondary
                )

                LazyColumn(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    itemsIndexed(profilePairs) { index, pair ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            OutlinedTextField(
                                value = pair.first,
                                onValueChange = { newKey ->
                                    profilePairs[index] = newKey to pair.second
                                },
                                label = { Text("Key") },
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                shape = MaterialTheme.shapes.small
                            )
                            OutlinedTextField(
                                value = pair.second,
                                onValueChange = { newValue ->
                                    profilePairs[index] = pair.first to newValue
                                },
                                label = { Text("Value") },
                                modifier = Modifier.weight(1.5f),
                                singleLine = true,
                                shape = MaterialTheme.shapes.small
                            )
                            IconButton(
                                onClick = { fieldIndexToRemove = index }
                            ) {
                                CloseIcon(tint = MaterialTheme.colorScheme.error)
                            }
                        }
                    }

                    item {
                        OutlinedButton(
                            onClick = { profilePairs.add("" to "") },
                            modifier = Modifier.fillMaxWidth().height(48.dp),
                            shape = MaterialTheme.shapes.medium
                        ) {
                            Text("+ Add Field", fontWeight = FontWeight.Bold)
                        }
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                Button(
                    onClick = {
                        scope.launch {
                            isLoading = true
                            val itemsToSave = profilePairs
                                .filter { it.first.isNotBlank() }
                                .map { ProfileItem(type = it.first.trim(), data = it.second.ifBlank { null }) }
                            
                            val request = SaveProfileRequest(items = itemsToSave)
                            repository.saveProfile(request).onSuccess { response ->
                                isLoading = false
                                if (response.error == null) {
                                    AppSession.userProfile = itemsToSave
                                    snackbarHostState.showSnackbar("Profile saved successfully")
                                    onBack()
                                } else {
                                    snackbarHostState.showSnackbar("Failed: ${response.error}")
                                }
                            }.onFailure {
                                isLoading = false
                                snackbarHostState.showSnackbar("Error: ${it.message}")
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(56.dp),
                    shape = MaterialTheme.shapes.medium,
                    enabled = !isLoading
                ) {
                    Text("Save Changes", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                }
            }

            if (isLoading) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    BrandLoadingSpinner()
                }
            }

            fieldIndexToRemove?.let { index ->
                ConfirmDeleteDialog(
                    title = "Remove field?",
                    message = "Remove '${profilePairs.getOrNull(index)?.first?.ifBlank { "this field" }}'?",
                    confirmLabel = "Remove",
                    onConfirm = {
                        if (index in profilePairs.indices) profilePairs.removeAt(index)
                        fieldIndexToRemove = null
                    },
                    onDismiss = { fieldIndexToRemove = null }
                )
            }
        }
    }
}
